import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Send, Loader2 } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';
import { toast } from 'sonner';
export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Client-side validation
    if (!formData.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    if (!validateEmail(formData.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!formData.service) {
      toast.error("Please select a service interest.");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Please enter a message about your project.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        toast.success("Message sent! I'll get back to you as soon as possible.");
        setFormData({ name: '', email: '', service: '', message: '' });
        (e.target as HTMLFormElement).reset();
      } else {
        toast.error(result.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };
  const handleServiceChange = (value: string) => {
    setFormData(prev => ({ ...prev, service: value }));
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="grid lg:grid-cols-2 gap-16">
        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Let's Work Together</h2>
            <p className="text-muted-foreground text-lg text-pretty">
              Whether you need an infrastructure audit or a full backend overhaul, I am ready to help you optimize and scale.
            </p>
          </div>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Mail className="text-blue-500" />
              </div>
              <div>
                <div className="font-semibold">Email Me</div>
                <div className="text-muted-foreground text-sm">hello@hemeratech.nl</div>
              </div>
            </div>
            {/* WhatsApp Clickable Link */}
            <a href="https://wa.me/31643019983" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-2 -ml-2 hover:bg-secondary/50 rounded-lg transition-colors group">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                <SiWhatsapp className="text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <div>
                <div className="font-semibold group-hover:text-green-500 transition-colors">WhatsApp</div>
                <div className="text-muted-foreground text-sm">+31 6 4301 9983</div>
              </div>
            </a>
          </div>
        </div>
        <div className="bg-card border border-border/50 rounded-3xl p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="Infra Wizard"
                  required
                  className="bg-secondary/50"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  required
                  className="bg-secondary/50"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Service Interest</Label>
              <Select
                onValueChange={handleServiceChange}
                value={formData.service}
                disabled={isSubmitting}
              >
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="infrastructure">Infrastructure Audit</SelectItem>
                  <SelectItem value="kubernetes">K8s Orchestration</SelectItem>
                  <SelectItem value="backend">Backend Development</SelectItem>
                  <SelectItem value="other">General Inquiry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Tell me about your project challenges..."
                className="min-h-[120px] bg-secondary/50"
                required
                value={formData.message}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
            </div>
            <Button type="submit" className="w-full h-12 text-md font-semibold" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  Sending... <Loader2 className="ml-2 w-4 h-4 animate-spin" />
                </>
              ) : (
                <>
                  Send Message <Send className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}